import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Collapse,
  LinearProgress,
  Pagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  useGetAdminExercisesQuery,
  useLazyGetAdminExerciseByIdQuery,
  useCreateExerciseMutation,
  useUpdateExerciseMutation,
  useDeleteExerciseMutation,
} from "@/store/api/admin/exerciseApi";
import { toast } from "react-toastify";
import RichTextEditor from "@/components/common/RichTextEditor";
import { RichTextDisplay } from "@/components/common";
import ExerciseQuestionManagement from "@/components/admin/ExerciseQuestionManagement";
import AttendanceTestQuestionManagement from "@/components/admin/AttendanceTestQuestionManagement";
import VocabularyManagement from "@/pages/admin/ExerciseManagement/VocabularyManagement";
import DayList from "./DayList";
import { EditorState } from "draft-js";

const ExerciseManagement = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    descriptionState: EditorState.createEmpty(),
  });
  const [expandedExercises, setExpandedExercises] = useState({});
  const [page, setPage] = useState(1);
  const limit = 6;

  // Question management state
  const [questionManagementOpen, setQuestionManagementOpen] = useState(false);
  const [questionManagementType, setQuestionManagementType] = useState(null);
  const [questionManagementDay, setQuestionManagementDay] = useState(null);
  const [questionManagementExerciseId, setQuestionManagementExerciseId] =
    useState(null);

  const { data: exercisesResponse, isLoading, error } = useGetAdminExercisesQuery({
    page,
    limit,
  });

  const exercises = exercisesResponse?.data || [];
  const totalPages = exercisesResponse?.data?.pagination?.totalPages || 1;
  const totalCount = exercisesResponse?.data?.pagination?.total || 0;

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [createExercise, { isLoading: isCreating }] =
    useCreateExerciseMutation();
  const [updateExercise, { isLoading: isUpdating }] =
    useUpdateExerciseMutation();
  const [deleteExercise] = useDeleteExerciseMutation();
  const [getExerciseById, { isLoading: isFetchingExercise }] = 
    useLazyGetAdminExerciseByIdQuery();

  const handleOpenDialog = async (exercise = null) => {
    if (exercise) {
      setDialogOpen(true);
      setEditingExercise(exercise);
      
      // Fetch fresh exercise data from API
      try {
        const result = await getExerciseById(exercise._id).unwrap();
        const freshExercise = result.data;
        
        setEditingExercise(freshExercise);
        setFormData({
          title: freshExercise.title,
          description: freshExercise.description || "",
          descriptionState:
            freshExercise.descriptionState || EditorState.createEmpty(),
        });
      } catch (error) {
        toast.error("Failed to load exercise details");
        // Fallback to cached data
        setFormData({
          title: exercise.title,
          description: exercise.description || "",
          descriptionState:
            exercise.descriptionState || EditorState.createEmpty(),
        });
      }
    } else {
      setEditingExercise(null);
      setFormData({
        title: "",
        description: "",
        descriptionState: EditorState.createEmpty(),
      });
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExercise(null);
    setFormData({
      title: "",
      description: "",
      descriptionState: EditorState.createEmpty(),
    });
  };

  const toggleExercise = (exerciseId) => {
    setExpandedExercises((prev) => ({
      ...prev,
      [exerciseId]: !prev[exerciseId],
    }));
  };

  // Question management handlers
  const handleOpenQuestionManagement = (exerciseId, day, partType) => {
    setQuestionManagementExerciseId(exerciseId);
    setQuestionManagementDay(day);
    setQuestionManagementType(partType);
    setQuestionManagementOpen(true);
  };

  const handleCloseQuestionManagement = () => {
    setQuestionManagementOpen(false);
    setQuestionManagementType(null);
    setQuestionManagementDay(null);
    setQuestionManagementExerciseId(null);
  };

  const getSortedDays = (exercise) => {
    const days = [...(exercise?.days || [])];
    days.sort((a, b) => a.dayNumber - b.dayNumber);
    return days;
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Exercise title is required");
      return;
    }

    try {
      if (editingExercise) {
        await updateExercise({
          exerciseId: editingExercise._id,
          ...formData,
        }).unwrap();

        toast.success("Exercise updated successfully");
      } else {
        await createExercise(formData).unwrap();
        toast.success("Exercise created successfully");
      }
      handleCloseDialog();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save exercise");
    }
  };

  const handleDelete = async (exercise) => {
    if (
      window.confirm(`Are you sure you want to delete "${exercise.title}"?`)
    ) {
      try {
        await deleteExercise(exercise._id).unwrap();
        toast.success("Exercise deleted successfully");
      } catch (error) {
        toast.error(error?.data?.message || "Failed to delete exercise");
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load exercises: {error?.data?.message || error.message}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1" fontWeight="bold">
          Exercise Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #1976D2 0%, #1565C0 100%)",
            },
          }}
        >
          Create Exercise
        </Button>
      </Box>

      {/* Exercises Grid */}
      <Grid container spacing={3}>
        {exercises?.map((exercise) => {
          const sortedDays = getSortedDays(exercise);
          const isExpanded = expandedExercises[exercise._id];

          return (
            <Grid item xs={12} key={exercise._id}>
              <Card
                elevation={2}
                sx={{
                  transition: "all 0.3s ease",
                  "&:hover": {
                    elevation: 8,
                  },
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box flexGrow={1}>
                      <Typography
                        variant="h5"
                        component="h2"
                        gutterBottom
                        fontWeight="bold"
                      >
                        {exercise.title}
                      </Typography>
                      {exercise.description && (
                        <Box sx={{ overflow: "hidden", mb: 2 }}>
                          <RichTextDisplay content={exercise.description} />
                        </Box>
                      )}
                      <Box mt={2} mb={2}>
                        <Chip
                          label={`${exercise.days?.length || 0} Days`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={exercise.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={exercise.isActive ? "success" : "default"}
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </Box>
                    <Box display="flex" gap={1}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(exercise)}
                        title="Edit Exercise"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(exercise)}
                        title="Delete Exercise"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => toggleExercise(exercise._id)}
                        title={isExpanded ? "Collapse Days" : "Expand Days"}
                      >
                        <ExpandMoreIcon
                          sx={{
                            transform: isExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.3s",
                          }}
                        />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Days Section */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box mt={3} pt={2} borderTop="1px solid #e0e0e0">
                      <Typography variant="h6" fontWeight="bold" mb={2}>
                        Days
                      </Typography>

                      <DayList
                        exercise={exercise}
                        handleOpenQuestionManagement={
                          handleOpenQuestionManagement
                        }
                      />
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4} width="100%">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2, alignSelf: 'center' }}>
            Total: {totalCount} exercises
          </Typography>
        </Box>
      )}

      {/* Empty State */}
      {exercises?.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="300px"
          textAlign="center"
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No exercises found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Create your first exercise to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Exercise
          </Button>
        </Box>
      )}

      {/* Create/Edit Exercise Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingExercise ? "Edit Exercise" : "Create New Exercise"}
          {isFetchingExercise && (
            <CircularProgress size={20} sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Exercise Title"
              value={formData.title}
              onChange={(e) => handleFormChange("title", e.target.value)}
              margin="normal"
              required
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Description (Optional)
              </Typography>
              <RichTextEditor
                value={formData.description}
                onChange={(content) => handleFormChange("description", content)}
                placeholder="Enter exercise description..."
                minHeight="150px"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isCreating || isUpdating}
          >
            {isCreating || isUpdating ? <CircularProgress size={20} /> : null}
            {editingExercise ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Question Management Components */}
      {questionManagementOpen && questionManagementDay && (
        <>
          {questionManagementType === "vocabulary" && (
            <VocabularyManagement
              exerciseId={questionManagementExerciseId}
              dayId={questionManagementDay._id}
              open={questionManagementOpen}
              onClose={handleCloseQuestionManagement}
            />
          )}
          {(questionManagementType === "lcExercise" ||
            questionManagementType === "rcExercise") && (
            <ExerciseQuestionManagement
              exerciseId={questionManagementExerciseId}
              dayId={questionManagementDay._id}
              partType={questionManagementType}
              open={questionManagementOpen}
              onClose={handleCloseQuestionManagement}
            />
          )}
          {questionManagementType === "attendanceTest" && (
            <AttendanceTestQuestionManagement
              exerciseId={questionManagementExerciseId}
              dayId={questionManagementDay._id}
              open={questionManagementOpen}
              onClose={handleCloseQuestionManagement}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default ExerciseManagement;
